import type {
  AddPageRequest,
  AlertList,
  AlertSettings,
  AuditPage,
  AuditQuery,
  AuthUser,
  Benchmark,
  ChangeFeedQuery,
  PageChangeRow,
  ReviewReport,
  SpeedResult,
  SpeedStrategy,
  ChangePasswordRequest,
  CreateUserRequest,
  ErrorLogGroup,
  ErrorLogRow,
  ExportFormat,
  LoginRequest,
  OpsMetrics,
  PageDetail,
  RecommendationList,
  RegistryPage,
  RegistryQuery,
  RegistryScope,
  ReportClientError,
  ReportSummary,
  ResetPasswordResult,
  ScanProgress,
  ScoreHistoryPoint,
  TrashRow,
  UpdatePageRequest,
  UpdateUserRequest,
  UserSummary,
} from 'shared';

/**
 * Cùng origin trong cả hai môi trường:
 * - dev: vite proxy `/api` sang localhost:3000
 * - prod: nginx proxy `/api/` sang container api
 *
 * Nhờ cùng origin nên cookie phiên tự đi kèm mọi request, không phải tự gắn
 * header Authorization và không phải giữ token trong JavaScript.
 */
const BASE = '/api';

/**
 * Lỗi "chưa đăng nhập / hết phiên", tách riêng để lớp trên phân biệt được với
 * lỗi thường và chuyển sang màn đăng nhập thay vì hiện thông báo đỏ.
 */
export class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Yêu cầu quét bị hết giờ ở reverse proxy — spec SC-09-B.
 *
 * Đây KHÔNG phải lỗi: việc quét vẫn đang chạy ở máy chủ, chỉ là kết nối HTTP
 * không chờ nổi. Tách ra để giao diện nói đúng câu ("tải lại sau ít phút") thay
 * vì hiện một dải đỏ khiến người dùng bấm quét lại lần nữa — làm máy chủ quét
 * đúp và càng lâu hơn.
 */
export class ScanStillRunningError extends Error {
  constructor() {
    super(
      'Việc quét vẫn đang chạy ở máy chủ, chỉ là phản hồi về chậm hơn thời gian chờ. ' +
        'Tải lại trang sau ít phút để xem kết quả — đừng bấm quét lại.',
    );
    this.name = 'ScanStillRunningError';
  }
}

/** Mã lỗi mà reverse proxy trả về khi yêu cầu chạy quá thời gian chờ của nó. */
const TIMEOUT_STATUS = new Set([408, 502, 503, 504]);

/**
 * Đường nào có thể chạy quá thời gian chờ của reverse proxy.
 *
 * Từ spec SC-08 §4.3 chỉ còn ĐÚNG MỘT: quét một trang. `scan-all` đã chuyển sang
 * hàng đợi nên trả về trong vài chục mili giây — nếu nó hết giờ thì đó là sự cố
 * thật, và nói "việc quét vẫn đang chạy ở máy chủ" lúc đó là nói sai.
 */
function isScanPath(path: string): boolean {
  return path.endsWith('/scan');
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (!res.ok) {
    if (isScanPath(path) && TIMEOUT_STATUS.has(res.status)) throw new ScanStillRunningError();

    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    const message = body?.message ?? `Lỗi ${res.status} khi gọi ${path}`;
    if (res.status === 401) throw new UnauthorizedError(message);
    // 429: máy chủ kèm `Retry-After`, dùng nó thay vì câu chung chung (spec SC-10).
    if (res.status === 429) {
      const sec = Number(res.headers.get('Retry-After'));
      throw new Error(
        Number.isFinite(sec) && sec > 0 ? `Bạn thao tác quá nhanh. Thử lại sau ${sec} giây.` : message,
      );
    }
    throw new Error(message);
  }

  // 204 không có body — `res.json()` sẽ ném lỗi nếu cứ gọi.
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Tải file về máy.
 *
 * Không dùng `<a download href>` trỏ thẳng vào API: đường tải là `POST` (mang
 * theo danh sách id được tick), mà thẻ `<a>` chỉ gửi được `GET`.
 */
async function download(path: string, body: unknown): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { message?: string } | null;
    if (res.status === 401) throw new UnauthorizedError(err?.message ?? 'Phiên đã hết hạn');
    throw new Error(err?.message ?? `Lỗi ${res.status} khi tải file`);
  }

  // Tên file do máy chủ đặt (có slug và mốc thời gian) — lấy từ Content-Disposition.
  const disposition = res.headers.get('Content-Disposition') ?? '';
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const plain = /filename="([^"]+)"/i.exec(disposition)?.[1];
  const fileName = utf8 ? decodeURIComponent(utf8) : (plain ?? 'seo-copilot.txt');

  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  // Thu hồi URL tạm, nếu không thì mỗi lần tải giữ lại một blob trong bộ nhớ tab.
  URL.revokeObjectURL(url);
}

export const api = {
  login: (body: LoginRequest) =>
    request<AuthUser>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<AuthUser>('/auth/me'),
  changePassword: (body: ChangePasswordRequest) =>
    request<void>('/auth/change-password', { method: 'POST', body: JSON.stringify(body) }),

  listUsers: () => request<UserSummary[]>('/users'),
  createUser: (body: CreateUserRequest) =>
    request<UserSummary>('/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: number, body: UpdateUserRequest) =>
    request<UserSummary>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /** Máy chủ sinh mật khẩu tạm và trả về ĐÚNG MỘT LẦN (spec SC-09-C). */
  resetUserPassword: (id: number) =>
    request<ResetPasswordResult>(`/users/${id}/reset-password`, { method: 'POST' }),

  /** Danh sách trang — lọc, tìm, sắp xếp và phân trang đều chạy ở máy chủ (SC-13 §2.3). */
  listRegistry: (q: Partial<RegistryQuery>) => request<RegistryPage>(`/registry?${queryString(q)}`),

  /* ── Thùng rác (spec SC-13 §2.1) ── */
  trash: () => request<TrashRow[]>('/registry/trash'),
  restorePage: (slug: string) =>
    request<PageDetail>(`/registry/${slug}/restore`, { method: 'POST' }),
  /** Xoá VĨNH VIỄN — mất toàn bộ lịch sử điểm, không hoàn tác được. */
  purgePage: (slug: string) => request<void>(`/registry/${slug}/purge`, { method: 'DELETE' }),
  addPage: (body: AddPageRequest) =>
    request<PageDetail>('/registry', { method: 'POST', body: JSON.stringify(body) }),
  updatePage: (slug: string, body: UpdatePageRequest) =>
    request<PageDetail>(`/registry/${slug}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePage: (slug: string) => request<void>(`/registry/${slug}`, { method: 'DELETE' }),
  /**
   * Xếp hàng quét cả danh sách — trả về NGAY một mã theo dõi (spec SC-08 §4.3).
   * Trước SC-08 đường này chờ tới khi quét xong; giờ tiến độ hỏi riêng bên dưới.
   */
  scanAll: (scope: RegistryScope = 'mine') =>
    request<ScanProgress>(`/registry/scan-all?scope=${scope}`, { method: 'POST' }),
  /** Không truyền `id` thì lấy lượt gần nhất, kể cả lượt định kỳ của hệ thống. */
  tienDoQuet: (id?: string) =>
    request<ScanProgress | null>(`/registry/quet/tien-do${id ? `?id=${encodeURIComponent(id)}` : ''}`),
  pageDetail: (slug: string) => request<PageDetail>(`/registry/${slug}`),
  scanOne: (slug: string) => request<PageDetail>(`/registry/${slug}/scan`, { method: 'POST' }),
  benchmark: (slug: string) => request<Benchmark[]>(`/registry/${slug}/benchmark`),

  /**
   * Đo tốc độ thật (spec SC-18). Google mất 10–40 giây nên đường này chậm HẲN so
   * với mọi lời gọi khác — giao diện phải nói rõ điều đó trong lúc chờ.
   */
  measureSpeed: (slug: string, strategy: SpeedStrategy = 'mobile') =>
    request<SpeedResult>(`/registry/${slug}/toc-do?strategy=${strategy}`, { method: 'POST' }),

  /* ── Rà soát cả danh sách (spec SC-17, SC-19) ── */
  reviewReport: (scope: RegistryScope = 'mine') =>
    request<ReviewReport>(`/review/doi-chieu?scope=${scope}`),
  changeFeed: (q: Partial<ChangeFeedQuery> & { scope?: RegistryScope }) =>
    request<PageChangeRow[]>(`/review/thay-doi?${queryString(q)}`),

  recommendations: (slug: string, scanId?: number) =>
    request<RecommendationList>(
      `/registry/${slug}/recommendations${scanId ? `?scanId=${scanId}` : ''}`,
    ),
  exportRecommendations: (slug: string, ids: string[], format: ExportFormat = 'huong-dan') =>
    download(`/registry/${slug}/recommendations/export`, { ids, format }),

  /**
   * Một file prompt gộp nhiều trang (spec SC-20 §3.2).
   *
   * Không nhận danh sách đề xuất: lấy trọn của lần quét mới nhất mỗi trang. Chọn
   * tay từng mục trên hai chục trang là việc không ai làm.
   */
  exportPrompt: (slugs: string[], boQuaTierC = false) =>
    download('/recommendations/export-prompt', { slugs, boQuaTierC }),

  /* ── Nhật ký kiểm toán (spec SC-12) ── */
  audit: (q: Partial<AuditQuery>) => request<AuditPage>(`/audit?${queryString(q)}`),
  /** Tải CSV theo đúng bộ lọc đang chọn. `GET` nên mở tab mới là đủ. */
  auditCsvUrl: (q: Partial<AuditQuery>) => `${BASE}/audit/export.csv?${queryString(q)}`,

  /* ── Báo cáo và cảnh báo (spec SC-08) ── */
  reportSummary: (scope: RegistryScope = 'mine') =>
    request<ReportSummary>(`/report/tong-hop?scope=${scope}`),
  /** Dữ liệu biểu đồ lịch sử điểm, đã sắp tăng dần theo thời gian. */
  lichSuDiem: (slug: string, thang?: number) =>
    request<ScoreHistoryPoint[]>(`/report/lich-su/${slug}${thang ? `?thang=${thang}` : ''}`),
  /**
   * Tải báo cáo dạng HTML in được (spec SC-08 §3.3).
   *
   * `download` gửi `POST` — không phải vì đường này đổi gì, mà vì tải qua `fetch`
   * mới kèm được cookie phiên và bắt được 401. Thẻ `<a download>` thì không.
   */
  xuatBaoCao: (scope: RegistryScope = 'mine') => download(`/report/xuat?scope=${scope}`, {}),
  xuatBaoCaoTrang: (slug: string) => download(`/report/xuat/${slug}`, {}),

  alerts: (chuaDoc = false) => request<AlertList>(`/alerts?chuaDoc=${chuaDoc}`),
  alertDaDoc: (id: number) => request<void>(`/alerts/${id}/da-doc`, { method: 'POST' }),
  alertDaDocHet: () => request<void>('/alerts/da-doc-het', { method: 'POST' }),
  alertNguong: () => request<AlertSettings>('/alerts/nguong'),
  datAlertNguong: (body: AlertSettings) =>
    request<AlertSettings>('/alerts/nguong', { method: 'PATCH', body: JSON.stringify(body) }),

  /* ── Quan sát vận hành (spec SC-16) ── */
  errorGroups: () => request<ErrorLogGroup[]>('/ops/errors'),
  errorsByRequestId: (requestId: string) =>
    request<ErrorLogRow[]>(`/ops/errors?requestId=${encodeURIComponent(requestId)}`),
  recentErrors: () => request<ErrorLogRow[]>('/ops/errors/recent'),
  opsMetrics: () => request<OpsMetrics>('/ops/metrics'),
  reportClientError: (body: ReportClientError) =>
    request<void>('/ops/client-error', { method: 'POST', body: JSON.stringify(body) }),
};

/** Bỏ hẳn tham số rỗng thay vì gửi `?actorId=` — server sẽ coi đó là giá trị sai kiểu. */
function queryString(q: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, v instanceof Date ? v.toISOString() : String(v));
  }
  return p.toString();
}
